import {FormattedMessage, useIntl} from "react-intl";
import {useEffect, useState} from "react";
import {GetColumnConfig} from "./API";

export function getTopicDisplayName(code, topicData, intl) {
    if (topicData?.taxonomy) {
        return intl.formatMessage({ id: `topic.${code}`, defaultMessage: code });
    }
    return topicData?.title || code;
}

export function getStudentFromContacts(student, contacts) {
    return contacts.find(contact => contact.id === student)
}

export function GetDifferenceText(dateDifference) {
    const time = getTimes(dateDifference)
    if (time.year > 0) {
        if (time.year > 1) {
           return <FormattedMessage
               id={"overNYearsAgo"}
               defaultMessage={"Over {n} years ago"}
               values={{n: time.year}}/>
        } else {
            return <FormattedMessage
                id={"overAYearAgo"}
                defaultMessage={"Over a year ago"} />
        }
    }
    if (time.month > 0) {
        if (time.month > 1) {
            return <FormattedMessage
                id={"overNMonthsAgo"}
                defaultMessage={"Over {n} months ago"}
                values={{n: time.month}}/>
        } else {
            return <FormattedMessage
                id={"overAMonthAgo"}
                defaultMessage={"Over a month ago"} />
        }
    }
    if (time.week > 0) {
        if (time.week > 1) {
            return <FormattedMessage
                id={"overNWeeksAgo"}
                defaultMessage={"Over {n} weeks ago"}
                values={{n: time.week}}/>
        } else {
            return <FormattedMessage
                id={"overAWeekAgo"}
                defaultMessage={"Over a week ago"} />
        }
    }
    if (time.day > 0) {
        if (time.day > 1) {
            return <FormattedMessage
                id={"overNDaysAgo"}
                defaultMessage={"Over {n} days ago"}
                values={{n: time.day}}/>
        } else {
            return <FormattedMessage
                id={"aDayAgo"}
                defaultMessage={"A day ago"} />
        }
    }
    if (time.hour > 0) {
        if (time.hour > 1) {
            return <FormattedMessage
                id={"overNHoursAgo"}
                defaultMessage={"Over {n} hours ago"}
                values={{n: time.hour}}/>
        } else {
            return <FormattedMessage
                id={"anHourAgo"}
                defaultMessage={"An hour ago"} />
        }
    }
    return <FormattedMessage
        id={"aMomentAgo"}
        defaultMessage={"A moment ago"}/>

}

export function getTimes(t) {
    let year, month, week, day, hour, minute, second

    second = Math.floor(t / 1000)
    minute = Math.floor(second / 60)
    second = second % 60
    hour = Math.floor(minute / 60)
    minute = minute % 60
    day = Math.floor(hour / 24)
    hour = hour % 24
    month = Math.floor(day / 30)
    week = Math.floor( day / 7 )
    day = day % 30
    year = Math.floor(month / 12)
    month = month % 12
    week = week % 52

    return { year, month, week, day, hour, minute, second }
}

export const useMediaQuery = (query) => {
    const [matches, setMatches] = useState(false);

    useEffect(() => {
        const media = window.matchMedia(query);
        if (media.matches !== matches) {
            setMatches(media.matches)
        }
        const listener = () => setMatches(media.matches)
        window.addEventListener("resize", listener)
        return () => window.removeEventListener("resize", listener)
    }, [matches, query])

    return matches
}

export function useWindowSize() {
    const [windowSize, setWindowSize] = useState({})

    useEffect(() => {
        function handleResize() {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            })
        }
        window.addEventListener("resize", handleResize)
        handleResize()
        return () => window.removeEventListener("resize", handleResize)
    }, [])

    return windowSize
}

export function formatDate(date) {
    if (!date) {
        return null
    }
    const formatted = date.replace(/\s/g, 'T') + ":00"
    return formatted
}

export function formatTranslationsToIntl(data) {
    if (!data) {
        return null
    }
    let intlObj = {}
    for (let i = 0; i < data.length; i++) {
        const langObj = data[i]
        intlObj[langObj.field] = langObj.value
    }
    return intlObj
}

export function transformGroupData(data) {
    let result = []

    if (Array.isArray(data?.noCampaignSurveys)) {
        data.noCampaignSurveys.forEach(item => {
            result.push({
                survey: {
                    id: [item.survey.id],
                    starttime: item.survey.starttime,
                    endtime: item.survey.endtime,
                    title: item.survey?.config?.title || item.survey.id,
                    contacts: item.survey.contacts
                },
                teachersupport: item.teachersupport,
                group: item.group
            })
        })
    }

    if (Array.isArray(data?.campaigns)) {
        data.campaigns.forEach(campaign => {
            let surveyIds = []
            let contacts = []
            let starttime, endtime

            if (Array.isArray(campaign?.surveys)) {
                campaign.surveys.forEach(surveyItem => {
                    surveyIds.push(surveyItem.survey.id);
                    contacts = [...contacts, ...surveyItem.survey.contacts]

                    // Update starttime and endtime to the most recent values
                    if (!starttime || new Date(surveyItem.survey.starttime) > new Date(starttime)) {
                        starttime = surveyItem.survey.starttime
                    }
                    if (!endtime || new Date(surveyItem.survey.endtime) > new Date(endtime)) {
                        endtime = surveyItem.survey.endtime
                    }
                })
            }

            let combinedGroup = {
                othersupport: [],
                notdelivered: [],
                startednotfinished: [],
                noresponse: [],
                everythingok: []
            }

            let combinedTeacherSupport = []

            if (Array.isArray(campaign?.surveys)) {
                campaign.surveys.forEach(surveyItem => {
                    // Combine group and teachersupport
                    Object.keys(combinedGroup).forEach(key => {
                        combinedGroup[key] = [...combinedGroup[key], ...surveyItem.group[key]]
                    })
                    combinedTeacherSupport = [...combinedTeacherSupport, ...surveyItem.teachersupport]
                })
            }

            result.push({
                survey: {
                    id: surveyIds,
                    starttime,
                    endtime,
                    title: campaign?.campaignName || "Missing Campaign Name",
                    contacts: Array.from(new Set(contacts)) // Remove duplicates
                },
                teachersupport: combinedTeacherSupport,
                group: combinedGroup
            })
        })
    }

    return result
}

export function findUserById(id, users, intl) {
    const user = users.find(user => user.id === id)

    if (!user) {
        if (id.startsWith('AU')) {
            return intl.formatMessage({
                defaultMessage: 'User not found',
                id: 'userInfoNotFound'
            })
        }
        return id
    }

    if (user?.meta && (user.meta?.firstname || user.meta?.lastname || user.meta?.userid || user.meta?.email)) {
        const { firstname = '', lastname = '', userid = '', email = '' } = user.meta
        const identification = userid ? userid : email ? email : ''

        if (firstname || lastname) {
            return `${firstname} ${lastname}${identification ? ' (' + identification + ')' : ''}`
        } else {
            return identification || id
        }
    } else {
        return id
    }
}

export function renderUser(user) {
    if (user?.meta && (user.meta?.firstname || user.meta?.lastname || user?.meta?.userid || user.meta?.email)) {
        const { firstname = '', lastname = '', role = '', email = '', userid = '' } = user.meta
        const identifier =  userid ? userid : email ? email : ''

        if (firstname || lastname) {
            return `${firstname} ${lastname}${role ? ' (' + role + ')' : ''}${identifier ? ' - ' + identifier : ''}`
        } else {
            return identifier || user?.id
        }
    } else {
        return user?.id
    }
}
